import type { Report } from '@/api/generated/model';
import type { ReportSubproduct as ApiReportSubproduct } from '@/api/generated/model/reportSubproduct';
import {
  ROI_REPORT_DEFAULT_ID,
  TOPICS_REPORT_DEFAULT_ID,
} from '@/domains/dashboard/components/report-sidebar-icon';

export type ReportSubproduct = ApiReportSubproduct;

export const DEFAULT_REPORT_SUBPRODUCT: ReportSubproduct = 'experience';

export const AIFI_REPORT_DEFAULT_ID = 'e8f9a0b1-c2d3-4e5f-6789-0abcdef12345';

const EXPERIENCE_ENTRY_DEFAULT_IDS = [TOPICS_REPORT_DEFAULT_ID] as const;

const REPORT_SUBPRODUCTS = new Set<ReportSubproduct>(['experience', 'roi', 'aifi']);

export function parseReportSubproduct(value: string | null | undefined): ReportSubproduct | null {
  if (!value) return null;
  return REPORT_SUBPRODUCTS.has(value as ReportSubproduct) ? (value as ReportSubproduct) : null;
}

export function inferReportSubproduct(defaultId: string | null | undefined): ReportSubproduct {
  if (defaultId === ROI_REPORT_DEFAULT_ID) return 'roi';
  if (defaultId === AIFI_REPORT_DEFAULT_ID) return 'aifi';
  return 'experience';
}

export function getReportSubproduct(report: Pick<Report, 'default_id' | 'subproduct'>): ReportSubproduct {
  return report.subproduct ?? inferReportSubproduct(report.default_id);
}

const REPORT_SUBPRODUCT_LABELS: Record<ReportSubproduct, string> = {
  experience: 'Experience',
  roi: 'ROI',
  aifi: 'Proficiency',
};

export function getReportSubproductLabel(subproduct: ReportSubproduct): string {
  return REPORT_SUBPRODUCT_LABELS[subproduct];
}

type SubproductReportCountInput = Pick<
  Report,
  'id' | 'default_id' | 'subproduct' | 'is_private' | 'is_favorite'
>;

export function countSubproductRailTooltipReports(
  reports: SubproductReportCountInput[],
  subproduct: ReportSubproduct,
  options?: { nebulyPageCount?: number },
): number {
  const ids = new Set<string>();

  for (const report of reports) {
    if (!report.is_private && getReportSubproduct(report) === subproduct) {
      ids.add(report.id);
    }
  }

  for (const report of reports) {
    if (report.is_favorite) {
      ids.add(report.id);
    }
  }

  for (const report of reports) {
    if (report.is_private) {
      ids.add(report.id);
    }
  }

  const nebulyPageCount = options?.nebulyPageCount ?? 0;
  return ids.size + nebulyPageCount;
}

export function findReportIdForSubproduct(
  reports: Report[],
  subproduct: ReportSubproduct,
): string | undefined {
  if (subproduct === 'roi') {
    return (
      reports.find((report) => report.default_id === ROI_REPORT_DEFAULT_ID)?.id ??
      reports.find((report) => getReportSubproduct(report) === 'roi')?.id
    );
  }

  if (subproduct === 'aifi') {
    return (
      reports.find((report) => report.default_id === AIFI_REPORT_DEFAULT_ID)?.id ??
      reports.find((report) => getReportSubproduct(report) === 'aifi')?.id
    );
  }

  for (const defaultId of EXPERIENCE_ENTRY_DEFAULT_IDS) {
    const preferred = reports.find((report) => report.default_id === defaultId);
    if (preferred) return preferred.id;
  }

  return (
    reports.find((report) => getReportSubproduct(report) === 'experience' && !report.is_private)?.id ??
    reports.find((report) => getReportSubproduct(report) === 'experience')?.id
  );
}

export function isReportSubproductVisible(
  subproduct: ReportSubproduct,
  projectProduct: 'external' | 'internal' | undefined,
): boolean {
  if (projectProduct === 'internal') return true;
  return subproduct === 'experience' || subproduct === 'aifi';
}

export function resolveReportSubproductForProject(
  subproduct: ReportSubproduct | null | undefined,
  projectProduct: 'external' | 'internal' | undefined,
): ReportSubproduct {
  const resolved = subproduct ?? DEFAULT_REPORT_SUBPRODUCT;
  return isReportSubproductVisible(resolved, projectProduct) ? resolved : DEFAULT_REPORT_SUBPRODUCT;
}
